/**
 * Camada única de detecção de plataforma + helpers nativos com fallback web.
 *
 * Regra de ouro: NADA aqui pode quebrar a versão web. Todos os plugins do
 * Capacitor são importados dinamicamente e só quando `isNative()` é verdadeiro,
 * de modo que o bundle do navegador nunca executa código nativo.
 */
import { Capacitor } from '@capacitor/core';

export type PlatformName = 'ios' | 'android' | 'web';

/** true apenas dentro do app empacotado (iOS/Android). */
export const isNative = (): boolean => Capacitor.isNativePlatform();

/** Plataforma atual: 'ios' | 'android' | 'web'. */
export const getPlatform = (): PlatformName => Capacitor.getPlatform() as PlatformName;

export const isIOS = (): boolean => getPlatform() === 'ios';
export const isAndroid = (): boolean => getPlatform() === 'android';
export const isWeb = (): boolean => !isNative();

/** Checa se um plugin nativo está disponível no runtime atual. */
export const hasPlugin = (name: string): boolean =>
  isNative() && Capacitor.isPluginAvailable(name);

/** Converte caminho de arquivo nativo (file://) em URL exibível no WebView. */
export const toDisplayableSrc = (pathOrUrl: string): string => {
  if (!pathOrUrl) return pathOrUrl;
  if (!isNative()) return pathOrUrl;
  if (/^(https?|data|blob):/i.test(pathOrUrl)) return pathOrUrl;
  try {
    return Capacitor.convertFileSrc(pathOrUrl);
  } catch {
    return pathOrUrl;
  }
};

/* ------------------------------------------------------------------ */
/* Geolocalização                                                      */
/* ------------------------------------------------------------------ */

export interface SimpleCoords {
  latitude: number;
  longitude: number;
  accuracy: number | null;
}

export interface GetPositionOptions {
  enableHighAccuracy?: boolean;
  timeoutMs?: number;
}

/**
 * Posição atual usando o plugin nativo no app e a Geolocation API no browser.
 * Lança erro se a permissão for negada — o chamador decide como tratar.
 */
export async function getCurrentPosition(
  options: GetPositionOptions = {},
): Promise<SimpleCoords> {
  const { enableHighAccuracy = true, timeoutMs = 15000 } = options;

  if (isNative()) {
    const { Geolocation } = await import('@capacitor/geolocation');
    const perm = await Geolocation.checkPermissions();
    if (perm.location !== 'granted') {
      const asked = await Geolocation.requestPermissions({ permissions: ['location'] });
      if (asked.location !== 'granted') throw new Error('location_permission_denied');
    }
    const pos = await Geolocation.getCurrentPosition({
      enableHighAccuracy,
      timeout: timeoutMs,
    });
    return {
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
      accuracy: pos.coords.accuracy ?? null,
    };
  }

  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new Error('geolocation_unsupported');
  }

  return new Promise<SimpleCoords>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy ?? null,
        }),
      (err) => reject(err),
      { enableHighAccuracy, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/* ------------------------------------------------------------------ */
/* Câmera / seleção de foto                                            */
/* ------------------------------------------------------------------ */

export interface PickedPhoto {
  /** URL pronta para <img src>. */
  src: string;
  /** Arquivo para upload (sempre presente na web; no nativo é convertido). */
  file: File | null;
}

/**
 * Abre a câmera (nativo) ou devolve `null` na web, deixando o chamador usar
 * o `<input type="file" capture="environment">` já existente na UI.
 */
export async function takePhoto(): Promise<PickedPhoto | null> {
  if (!isNative()) return null;

  const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
  const photo = await Camera.getPhoto({
    quality: 80,
    allowEditing: false,
    resultType: CameraResultType.Uri,
    source: CameraSource.Camera,
    correctOrientation: true,
  });

  const src = photo.webPath ? toDisplayableSrc(photo.webPath) : '';
  let file: File | null = null;
  if (photo.webPath) {
    try {
      const blob = await (await fetch(photo.webPath)).blob();
      file = new File([blob], `photo.${photo.format || 'jpeg'}`, {
        type: blob.type || 'image/jpeg',
      });
    } catch {
      file = null;
    }
  }
  return { src, file };
}

/**
 * Selfie nativa (câmera frontal) devolvida em base64 puro, pronta para a
 * edge function `verify-selfie`. Retorna `null` na web — lá continuamos
 * usando `getUserMedia` + canvas.
 */
export async function takeSelfieBase64(): Promise<string | null> {
  if (!isNative()) return null;
  const { Camera, CameraResultType, CameraSource, CameraDirection } = await import(
    '@capacitor/camera'
  );
  const currentPermission = await Camera.checkPermissions();
  if (currentPermission.camera !== 'granted') {
    const requestedPermission = await Camera.requestPermissions({ permissions: ['camera'] });
    if (requestedPermission.camera !== 'granted') {
      throw new Error('camera_permission_denied');
    }
  }
  const photo = await Camera.getPhoto({
    quality: 80,
    allowEditing: false,
    resultType: CameraResultType.Base64,
    source: CameraSource.Camera,
    direction: CameraDirection.Front,
    correctOrientation: true,
    width: 640,
    height: 640,
  });
  return photo.base64String ?? null;
}

/* ------------------------------------------------------------------ */
/* Haptics                                                             */
/* ------------------------------------------------------------------ */

export type HapticStrength = 'light' | 'medium' | 'heavy';

/** Vibração curta: Haptics no app, Vibration API no browser (quando existir). */
export async function haptic(strength: HapticStrength = 'light'): Promise<void> {
  try {
    if (isNative()) {
      const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
      const style =
        strength === 'heavy'
          ? ImpactStyle.Heavy
          : strength === 'medium'
            ? ImpactStyle.Medium
            : ImpactStyle.Light;
      await Haptics.impact({ style });
      return;
    }
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(strength === 'heavy' ? 30 : strength === 'medium' ? 20 : 10);
    }
  } catch {
    /* haptics nunca deve quebrar a UI */
  }
}

export async function hapticNotification(type: 'success' | 'warning' | 'error' = 'success') {
  try {
    if (!isNative()) return haptic(type === 'error' ? 'heavy' : 'light');
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    const map = {
      success: NotificationType.Success,
      warning: NotificationType.Warning,
      error: NotificationType.Error,
    } as const;
    await Haptics.notification({ type: map[type] });
  } catch {
    /* noop */
  }
}

/* ------------------------------------------------------------------ */
/* Status bar / splash                                                 */
/* ------------------------------------------------------------------ */

/** Aplica o visual dark do Zappy na status bar (no-op na web). */
export async function applyNativeChrome(backgroundColor = '#0A1310'): Promise<void> {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Dark });
    if (isAndroid()) await StatusBar.setBackgroundColor({ color: backgroundColor });
  } catch {
    /* noop */
  }
}

/** Esconde a splash nativa assim que o app estiver interativo (no-op na web). */
export async function hideSplashScreen(): Promise<void> {
  if (!isNative()) return;
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch {
    /* noop */
  }
}

/* ------------------------------------------------------------------ */
/* Push notifications                                                  */
/* ------------------------------------------------------------------ */

/**
 * Registra o device para push nativo e devolve o token (APNs/FCM).
 * Na web devolve `null` — o fluxo Web Push existente continua responsável.
 */
export async function registerNativePush(): Promise<string | null> {
  if (!isNative()) return null;
  const { PushNotifications } = await import('@capacitor/push-notifications');

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== 'granted') return null;

  return new Promise<string | null>((resolve) => {
    const timeout = setTimeout(() => resolve(null), 15000);
    PushNotifications.addListener('registration', (token) => {
      clearTimeout(timeout);
      resolve(token.value);
    });
    PushNotifications.addListener('registrationError', () => {
      clearTimeout(timeout);
      resolve(null);
    });
    void PushNotifications.register();
  });
}

/* ------------------------------------------------------------------ */
/* Ciclo de vida do app                                                */
/* ------------------------------------------------------------------ */

/**
 * Observa foreground/background. No nativo usa @capacitor/app; na web usa
 * `visibilitychange`. Devolve função de cleanup em ambos os casos.
 */
export function onAppStateChange(handler: (isActive: boolean) => void): () => void {
  if (isNative()) {
    let remove: (() => void) | null = null;
    void import('@capacitor/app').then(async ({ App }) => {
      const sub = await App.addListener('appStateChange', ({ isActive }) => handler(isActive));
      remove = () => void sub.remove();
    });
    return () => remove?.();
  }

  if (typeof document === 'undefined') return () => {};
  const listener = () => handler(document.visibilityState === 'visible');
  document.addEventListener('visibilitychange', listener);
  return () => document.removeEventListener('visibilitychange', listener);
}

/** Botão "voltar" do Android (no-op na web). Devolve cleanup. */
export function onHardwareBackButton(handler: () => void): () => void {
  if (!isAndroid()) return () => {};
  let remove: (() => void) | null = null;
  void import('@capacitor/app').then(async ({ App }) => {
    const sub = await App.addListener('backButton', () => handler());
    remove = () => void sub.remove();
  });
  return () => remove?.();
}
